// Installs a one-time wrapper around window.fetch that attaches the signer's
// session credentials (from the page URL) to same-origin /api requests as the
// x-session-token / x-session-user headers.
//
// This is the single choke point that lets the signature write endpoints
// enforce per-user authorization without editing every call site. It only ADDS
// headers - it never changes the method, body, or other headers of a request -
// so it is safe for all existing callers (including the offline replay queue,
// which also goes through window.fetch).

export function installSessionAuthFetch(): void {
  if (typeof window === 'undefined') return;
  const w = window as unknown as { __sessionAuthFetchInstalled?: boolean };
  if (w.__sessionAuthFetchInstalled) return;
  w.__sessionAuthFetchInstalled = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    try {
      const url =
        typeof input === 'string'
          ? input
          : input instanceof URL
          ? input.toString()
          : input.url;

      const isSameOriginApi =
        url.startsWith('/api/') || url.startsWith(`${window.location.origin}/api/`);

      if (isSameOriginApi) {
        const params = new URLSearchParams(window.location.search);
        const token = params.get('token');
        const userId = params.get('userId');

        if (token && userId) {
          // Seed from whichever headers the caller already set so we preserve
          // Content-Type etc.; init.headers wins when both are present.
          const headers = new Headers(
            init?.headers ?? (input instanceof Request ? input.headers : undefined),
          );
          if (!headers.has('x-session-token')) headers.set('x-session-token', token);
          if (!headers.has('x-session-user')) headers.set('x-session-user', userId);
          init = { ...init, headers };
        }
      }
    } catch {
      // Never let header injection break a request.
    }

    return originalFetch(input as RequestInfo, init);
  };
}
